import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStorage, ref } from "firebase/storage";
import { getSignedUrl, isFirebaseStorageUrl } from "../services/Firebase";
import { MediaItem } from "../models/Post";
import { MediaPlayerControls } from "./MediaPlayerControls";
import { RemotionOverlayRenderer } from "../remotion/RemotionOverlayRenderer";
import { resolveImageFilterCss } from "../utils/imageFilter";

interface MediaPlayerProps {
  mediaItem: MediaItem;
  currentTime?: number;
  onTimeUpdate?: (time: number) => void;
  onPlayStateChange?: (isPlaying: boolean) => void;
  onMediaLoad?: (duration?: number) => void;
  onSeek?: (time: number) => void;
  isControlsVisible?: boolean;
  videoElementId?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  isFreePlan?: boolean;
  mediaUrls?: Record<string, string>;
  lazyLoadOverlays?: boolean;
  borderless?: boolean;
}

export function MediaPlayer({
  mediaItem,
  currentTime,
  onTimeUpdate,
  onPlayStateChange,
  onMediaLoad,
  onSeek,
  isControlsVisible = true,
  videoElementId,
  autoPlay = false,
  loop = false,
  muted = false,
  isFreePlan = false,
  mediaUrls,
  lazyLoadOverlays = false,
  borderless = false,
}: MediaPlayerProps) {
  const { overlays, audioSettings, thumbnailPath } = mediaItem.metadata || {};
  const mediaType = mediaItem.type;
  const wasGeneratedWithWatermark = mediaItem.metadata?.generatedWithWatermark || false;

  const [mediaDimensions, setMediaDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [mediaDimensionsReady, setMediaDimensionsReady] = useState(false);
  const [scaledMediaDimensions, setScaledMediaDimensions] = useState({
    width: 0,
    height: 0,
    left: 0,
    top: 0,
  });

  useEffect(() => {
    const calculateScaledDimensions = () => {
      if (
        !containerRef.current ||
        !mediaDimensions.width ||
        !mediaDimensions.height
      ) {
        return;
      }

      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;
      const mediaAspectRatio = mediaDimensions.width / mediaDimensions.height;
      const containerAspectRatio = containerWidth / containerHeight;

      let newWidth = containerWidth;
      let newHeight = containerHeight;
      let newLeft = 0;
      let newTop = 0;

      if (mediaAspectRatio > containerAspectRatio) {
        newHeight = containerWidth / mediaAspectRatio;
        newTop = (containerHeight - newHeight) / 2;
      } else {
        newWidth = containerHeight * mediaAspectRatio;
        newLeft = (containerWidth - newWidth) / 2;
      }

      setScaledMediaDimensions({
        width: newWidth,
        height: newHeight,
        left: newLeft,
        top: newTop,
      });
    };

    calculateScaledDimensions();

    const resizeObserver = new ResizeObserver(calculateScaledDimensions);
    const currentContainer = containerRef.current;
    if (currentContainer) {
      resizeObserver.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        resizeObserver.unobserve(currentContainer);
      }
    };
  }, [mediaDimensions, mediaDimensionsReady]);

  const [resolvedMediaUrl, setResolvedMediaUrl] = useState<string | null>(
    () => mediaUrls?.[mediaItem.path] ?? null
  );
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [videoTime, setVideoTime] = useState(currentTime || 0);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [syntheticPosterUrl, setSyntheticPosterUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(muted);

  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const playTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasEndedRef = useRef(false);
  const videoEndedDurationRef = useRef<number>(0);

  const isPlayingRef = useRef(isPlaying);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const [isInViewport, setIsInViewport] = useState(!lazyLoadOverlays);

  useEffect(() => {
    if (!lazyLoadOverlays || !containerRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = Math.random() * 300;
            setTimeout(() => {
              setIsInViewport(true);
            }, delay);
          } else {
            setTimeout(() => {
              setIsInViewport(false);
            }, 500);
          }
        });
      },
      {
        rootMargin: "300px",
        threshold: 0.01,
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [lazyLoadOverlays]);

  const onTimeUpdateRef = useRef(onTimeUpdate);
  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);

  const onPlayStateChangeRef = useRef(onPlayStateChange);
  useEffect(() => {
    onPlayStateChangeRef.current = onPlayStateChange;
  }, [onPlayStateChange]);

  // Effect to resolve the main media URL
  useEffect(() => {
    const resolveMediaUrl = async () => {
      const inputPath = mediaItem.path;

      if (!inputPath) {
        setResolvedMediaUrl(null);
        return;
      }

      if (mediaUrls && mediaUrls[inputPath]) {
        setResolvedMediaUrl(mediaUrls[inputPath]);
        return;
      }

      if (inputPath.startsWith("posts/") || isFirebaseStorageUrl(inputPath)) {
        try {
          const url = await import("../services/Firebase").then((module) =>
            module.getSignedUrl(inputPath)
          );
          setResolvedMediaUrl(url);
        } catch (error) {
          console.error("MediaPlayer: Error fetching signed URL:", error);
          setResolvedMediaUrl(null);
        }
      } else {
        setResolvedMediaUrl(inputPath);
      }
    };

    resolveMediaUrl();
  }, [mediaItem.path, mediaUrls]);

  // Effect to fetch thumbnail URL
  useEffect(() => {
    const fetchThumbnail = async () => {
      if (mediaType === "video" && thumbnailPath) {
        try {
          if (mediaUrls && mediaUrls[thumbnailPath]) {
            setThumbnailUrl(mediaUrls[thumbnailPath]);
            return;
          }

          if (
            thumbnailPath.startsWith("posts/") ||
            isFirebaseStorageUrl(thumbnailPath)
          ) {
            const url = await import("../services/Firebase").then((module) =>
              module.getSignedUrl(thumbnailPath)
            );
            setThumbnailUrl(url);
          } else {
            setThumbnailUrl(thumbnailPath);
          }
        } catch (error) {
          console.error("Error fetching thumbnail:", error);
          setThumbnailUrl(null);
        }
      } else {
        setThumbnailUrl(null);
      }
    };

    fetchThumbnail();
  }, [mediaType, thumbnailPath, mediaUrls]);

  // Generate synthetic poster from video
  useEffect(() => {
    let isCancelled = false;
    const generatePoster = async () => {
      if (
        mediaType !== "video" ||
        thumbnailUrl ||
        !resolvedMediaUrl ||
        typeof mediaItem.metadata.coverTimestamp !== "number"
      ) {
        setSyntheticPosterUrl(null);
        return;
      }
      try {
        const video = document.createElement("video");
        video.crossOrigin = "anonymous";
        video.preload = "auto";
        video.muted = true;
        video.playsInline = true;
        await new Promise<void>((resolve, reject) => {
          const onLoaded = () => {
            video.removeEventListener("loadedmetadata", onLoaded);
            resolve();
          };
          const onError = (e: Event) => {
            video.removeEventListener("loadedmetadata", onLoaded);
            reject(e);
          };
          video.addEventListener("loadedmetadata", onLoaded, { once: true });
          video.addEventListener("error", onError, { once: true });
          video.src = resolvedMediaUrl;
          video.load();
        });

        const targetTime = Math.max(
          0,
          (mediaItem.metadata.coverTimestamp || 0) / 1000
        );
        await new Promise<void>((resolve, reject) => {
          const cleanup = () => {
            video.removeEventListener("seeked", onSeeked);
            video.removeEventListener("canplay", onCanPlay);
            video.removeEventListener("error", onError);
          };
          const onSeeked = () => {
            if (video.readyState >= 2) {
              cleanup();
              resolve();
            }
          };
          const onCanPlay = () => {
            cleanup();
            resolve();
          };
          const onError = (e: Event) => {
            cleanup();
            reject(e);
          };
          video.addEventListener("seeked", onSeeked, { once: true });
          video.addEventListener("canplay", onCanPlay, { once: true });
          video.addEventListener("error", onError, { once: true });
          try {
            video.currentTime = Math.min(
              targetTime,
              video.duration || targetTime
            );
          } catch (e) {
            setTimeout(() => {
              try {
                video.currentTime = Math.min(
                  targetTime,
                  video.duration || targetTime
                );
              } catch {}
            }, 50);
          }
        });

        if (isCancelled) return;
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("No 2D context");
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        if (!isCancelled) setSyntheticPosterUrl(dataUrl);
      } catch (e) {
        console.warn("Failed generating synthetic poster", e);
        if (!isCancelled) setSyntheticPosterUrl(null);
      }
    };

    generatePoster();
    return () => {
      isCancelled = true;
    };
  }, [
    mediaType,
    thumbnailUrl,
    resolvedMediaUrl,
    mediaItem.metadata.coverTimestamp,
  ]);

  // Handle initial setup
  useEffect(() => {
    if (videoRef.current) {
      const videoElement = videoRef.current;

      hasEndedRef.current = false;
      videoElement.currentTime = 0;
      videoElement.pause();
      setIsPlaying(false);
      setIsInitialized(false);

      const handleMetadata = () => {
        setIsInitialized(true);
        setVideoDuration(videoElement.duration);
      };

      videoElement.addEventListener("loadedmetadata", handleMetadata);

      return () => {
        videoElement.removeEventListener("loadedmetadata", handleMetadata);
      };
    }
  }, [mediaItem.path]);

  // Handle autoplay
  useEffect(() => {
    if (!isInitialized || !videoRef.current || !autoPlay) return;

    const videoElement = videoRef.current;

    if (videoElement.paused) {
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise.catch((error: Error) => {
          console.error("AutoPlay failed:", error);
          setIsPlaying(false);
          onPlayStateChangeRef.current?.(false);
        });
      }
    }
  }, [isInitialized, autoPlay]);

  // Handle time updates from parent
  useEffect(() => {
    if (
      !isInitialized ||
      !videoRef.current ||
      currentTime === undefined
    )
      return;

    const videoElement = videoRef.current;
    const timeDifference = Math.abs(videoElement.currentTime - currentTime);

    if (timeDifference > 0.5) {
      hasEndedRef.current = false;
      videoElement.currentTime = currentTime;
      onSeek?.(currentTime);
    } else if (Math.abs(videoElement.currentTime - currentTime) > 0.1) {
      videoElement.currentTime = currentTime;
    }
  }, [
    isInitialized,
    currentTime,
    onSeek,
  ]);

  const syncAudioWithVideo = () => {
    if (!audioRef.current || !videoRef.current || !audioSettings) return;

    const audioElement = audioRef.current;
    const videoElement = videoRef.current;
    const videoCurrentTime = videoElement.currentTime;

    const audioStartTime = audioSettings.startTime || 0;
    const audioEndTime = audioSettings.endTime || videoElement.duration;
    const audioDuration = audioEndTime - audioStartTime;

    const isWithinAudioRelevance = videoCurrentTime < audioDuration;

    if (isWithinAudioRelevance) {
      const expectedAudioTime = audioStartTime + videoCurrentTime;
      const timeDiff = Math.abs(audioElement.currentTime - expectedAudioTime);

      if (timeDiff > 0.3) {
        audioElement.currentTime = expectedAudioTime;

        if (videoElement.paused === false && audioElement.paused === true) {
          if (audioSettings.volume !== undefined) {
            audioElement.volume = audioSettings.volume / 100;
          }

          audioElement.play().catch((err) => {
            console.error("Error restarting audio:", err);
          });
        }
      }
    } else {
      if (!audioElement.paused) {
        audioElement.pause();
      }
    }
  };

  // Apply audio and video volume settings
  useEffect(() => {
    if (audioRef.current && audioSettings) {
      const volume =
        audioSettings.volume !== undefined ? audioSettings.volume / 100 : 1;
      audioRef.current.volume = volume;
      audioRef.current.muted = isMuted;
    }

    if (videoRef.current && audioSettings) {
      const videoVolume =
        audioSettings.videoVolume !== undefined
          ? audioSettings.videoVolume / 100
          : 1;
      videoRef.current.volume = videoVolume;
      videoRef.current.muted = videoVolume === 0 || isMuted;
    } else if (videoRef.current) {
      videoRef.current.volume = 1;
      videoRef.current.muted = isMuted;
    }
  }, [audioSettings, isMuted]);

  const handleMediaLoad = () => {
    if (videoRef.current) {
      const videoElement = videoRef.current;

      if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
        setMediaDimensions({
          width: videoElement.videoWidth,
          height: videoElement.videoHeight,
        });
        setMediaDimensionsReady(true);

        if (onMediaLoad) {
          onMediaLoad(videoElement.duration);
        }

        if (audioSettings) {
          const videoVolume =
            audioSettings.videoVolume !== undefined
              ? audioSettings.videoVolume / 100
              : 1;
          videoElement.volume = videoVolume;
          videoElement.muted = videoVolume === 0 || isMuted;
        }
      }
    }
  };

  useEffect(() => {
    if (mediaType === "video" && videoRef.current) {
      const videoElement = videoRef.current;

      const handleLoaded = () => {
        if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
          setMediaDimensions({
            width: videoElement.videoWidth,
            height: videoElement.videoHeight,
          });
          setMediaDimensionsReady(true);
        }
      };

      if (videoElement.readyState >= 2) {
        handleLoaded();
      } else {
        videoElement.addEventListener("loadedmetadata", handleLoaded);
        return () => {
          videoElement.removeEventListener("loadedmetadata", handleLoaded);
        };
      }
    }
  }, [mediaType]);

  useEffect(() => {
    const videoNode = videoRef.current;
    const audioNode = audioRef.current;
    const timeout = playTimeoutRef.current;

    return () => {
      if (videoNode) {
        videoNode.pause();
      }
      if (audioNode) {
        audioNode.pause();
      }
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  useEffect(() => {
    if (!mediaDimensionsReady && mediaItem.metadata.dimensions) {
      setMediaDimensions({
        width: mediaItem.metadata.dimensions.width,
        height: mediaItem.metadata.dimensions.height,
      });
      setMediaDimensionsReady(true);
    }
  }, [mediaDimensionsReady, mediaItem.metadata.dimensions]);

  useEffect(() => {
    const fetchAudioUrl = async () => {
      if (mediaType === "video" && audioSettings?.audioPath) {
        try {
          const audioPath = audioSettings.audioPath;
          if (!audioPath) {
            setAudioUrl(null);
            return;
          }

          if (
            (!audioPath.startsWith("http") && !audioPath.startsWith("blob:")) ||
            isFirebaseStorageUrl(audioPath)
          ) {
            const url = await import("../services/Firebase").then((module) =>
              module.getSignedUrl(audioPath)
            );
            setAudioUrl(url);
          } else {
            setAudioUrl(audioPath);
          }
        } catch (error) {
          console.error("Error fetching audio URL:", error);
          setAudioUrl(null);
        }
      } else {
        setAudioUrl(null);
      }
    };

    fetchAudioUrl();
  }, [mediaType, audioSettings?.audioPath]);

  const [overlayImageUrls, setOverlayImageUrls] = useState<Record<string, string>>({});
  const loadedOverlayUrlsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let isMounted = true;

    const loadOverlayImageUrls = async () => {
      if (!overlays || overlays.length === 0) {
        return;
      }

      const storage = getStorage();
      const newUrls: Record<string, string> = {};

      const imageOverlaysToLoad = overlays.filter(
        (overlay) =>
          overlay.type === "image" &&
          "filePath" in overlay &&
          overlay.filePath &&
          !mediaUrls?.[overlay.filePath] &&
          !loadedOverlayUrlsRef.current.has(overlay.filePath)
      );

      if (imageOverlaysToLoad.length === 0) {
        return;
      }

      for (const overlay of imageOverlaysToLoad) {
        if ("filePath" in overlay && overlay.filePath) {
          try {
            const storageRef = ref(storage, overlay.filePath);
            const url = await getSignedUrl(storageRef.fullPath);
            newUrls[overlay.filePath] = url;
          } catch (error) {
            console.error("Error loading overlay image URL:", error, overlay.filePath);
          }
        }
      }

      if (isMounted && Object.keys(newUrls).length > 0) {
        // Only mark as loaded AFTER we successfully update state,
        // so that if the effect was cleaned up mid-flight, the next
        // run will retry these URLs.
        for (const filePath of Object.keys(newUrls)) {
          loadedOverlayUrlsRef.current.add(filePath);
        }
        setOverlayImageUrls((prev) => ({ ...prev, ...newUrls }));
      }
    };

    loadOverlayImageUrls();

    return () => {
      isMounted = false;
    };
  }, [overlays, mediaUrls]);

  // Only include URLs relevant to this MediaPlayer's overlays.
  // This prevents unrelated URL additions (from other posts) from changing
  // the playerKey in RemotionOverlayRenderer and causing unnecessary remounts.
  const mergedImageUrls = useMemo(() => {
    if (!overlays || overlays.length === 0) return {};
    const relevant: Record<string, string> = {};
    for (const overlay of overlays) {
      if (overlay.type === "image" && "filePath" in overlay && overlay.filePath) {
        const url = overlayImageUrls[overlay.filePath] || mediaUrls?.[overlay.filePath];
        if (url) {
          relevant[overlay.filePath] = url;
        }
      }
    }
    return relevant;
  }, [overlays, mediaUrls, overlayImageUrls]);

  const showThumbnail = thumbnailUrl && !isPlaying && videoTime === 0;
  const showSyntheticPoster =
    !thumbnailUrl &&
    mediaType === "video" &&
    !isPlaying &&
    videoTime === 0 &&
    typeof mediaItem.metadata.coverTimestamp === "number" &&
    !!syntheticPosterUrl;

  const isVideoElementVisible =
    (!thumbnailUrl && !showSyntheticPoster) ||
    isPlaying ||
    videoTime > 0 ||
    (mediaType === "video" && !!resolvedMediaUrl);
  const showLoadingIndicator =
    (!resolvedMediaUrl || !mediaDimensionsReady) && mediaItem.path;

  const shouldRenderOverlays =
    isInViewport &&
    (mediaType === "image" ||
      (mediaType === "video" &&
        isVideoElementVisible &&
        !showThumbnail &&
        !showSyntheticPoster)) &&
    resolvedMediaUrl &&
    overlays &&
    overlays.length > 0 &&
    mediaDimensionsReady &&
    scaledMediaDimensions.width > 0;

  const totalDuration = videoDuration;

  return (
    <div
      ref={containerRef}
      className={`relative aspect-video overflow-hidden bg-black w-full h-full ${borderless ? "" : "rounded-lg border"}`}
      style={{ maxWidth: "100%", maxHeight: "100%" }}
    >
      {/* Main media (video or image) */}
      {mediaType === "video" ? (
        <>
          <video
            id={videoElementId}
            ref={videoRef}
            src={resolvedMediaUrl ?? undefined}
            className="w-full h-full object-contain"
            style={{
              objectFit: mediaItem.metadata.coverViewMode || "contain",
              pointerEvents: isControlsVisible ? "auto" : "none",
              display:
                showThumbnail || showSyntheticPoster ? "none" : "block",
            }}
            onLoadedData={handleMediaLoad}
            controls={false}
            playsInline
            onPlay={() => {
              if (!isInitialized) return;
              hasEndedRef.current = false;
              setIsPlaying(true);
              onPlayStateChangeRef.current?.(true);

              if (audioRef.current && audioUrl) {
                syncAudioWithVideo();
              }
            }}
            onPause={() => {
              if (!isInitialized) return;
              if (
                videoRef.current &&
                videoRef.current.currentTime >=
                  videoRef.current.duration - 0.1
              ) {
                return;
              }
              setIsPlaying(false);
              onPlayStateChangeRef.current?.(false);

              if (audioRef.current && !audioRef.current.paused) {
                audioRef.current.pause();
              }
            }}
            onTimeUpdate={(e) => {
              if (!isInitialized) return;
              const video = e.currentTarget;
              const newTime = video.currentTime;
              const duration = video.duration;

              if (
                duration > 0 &&
                newTime >= duration - 0.1 &&
                !hasEndedRef.current
              ) {
                hasEndedRef.current = true;

                setIsPlaying(false);
                onPlayStateChangeRef.current?.(false);

                if (audioRef.current && !audioRef.current.paused) {
                  audioRef.current.pause();
                }

                if (loop && videoRef.current) {
                  videoRef.current.currentTime = 0;
                  videoRef.current.play();
                } else {
                  setVideoTime(0);
                  onTimeUpdateRef.current?.(0);
                  hasEndedRef.current = false;
                }
                return;
              }

              if (duration - newTime > 0.1) {
                setVideoTime(newTime);
                onTimeUpdateRef.current?.(newTime);

                if (isPlaying && audioRef.current && audioUrl) {
                  syncAudioWithVideo();
                }
              }
            }}
          />

          {/* Thumbnail overlay */}
          {showThumbnail && (
            <div
              className="absolute inset-0 w-full h-full group cursor-pointer"
              onClick={() => {
                if (isControlsVisible && videoRef.current) {
                  setIsPlaying(true);
                  const controlsVisible = videoRef.current.controls;
                  videoRef.current.controls = false;
                  videoRef.current.currentTime = 0;
                  setTimeout(() => {
                    if (videoRef.current) {
                      const playPromise = videoRef.current.play();
                      videoRef.current.controls = controlsVisible;
                      if (playPromise !== undefined) {
                        playPromise.catch((err) => {
                          console.error("Error playing video:", err);
                          setIsPlaying(false);
                          onPlayStateChangeRef.current?.(false);
                        });
                      }
                    }
                  }, 50);
                }
              }}
            >
              <img
                src={thumbnailUrl}
                alt="Video thumbnail"
                className="w-full h-full"
                style={{
                  objectFit: mediaItem.metadata.coverViewMode || "contain",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-16 h-16 rounded-full bg-white/25 flex items-center justify-center">
                  <div className="w-0 h-0 border-t-[12px] border-t-transparent border-l-[24px] border-l-white border-b-[12px] border-b-transparent ml-2" />
                </div>
              </div>
            </div>
          )}

          {/* Synthetic poster */}
          {showSyntheticPoster && (
            <div
              className="absolute inset-0 w-full h-full group cursor-pointer"
              onClick={() => {
                if (isControlsVisible && videoRef.current) {
                  setIsPlaying(true);
                  const controlsVisible = videoRef.current.controls;
                  videoRef.current.controls = false;
                  videoRef.current.currentTime = 0;
                  setTimeout(() => {
                    if (videoRef.current) {
                      const playPromise = videoRef.current.play();
                      videoRef.current.controls = controlsVisible;
                      if (playPromise !== undefined) {
                        playPromise.catch((err) => {
                          console.error("Error playing video:", err);
                          setIsPlaying(false);
                          onPlayStateChangeRef.current?.(false);
                        });
                      }
                    }
                  }, 50);
                }
              }}
            >
              {syntheticPosterUrl && (
                <img
                  src={syntheticPosterUrl}
                  alt="Video frame"
                  className="w-full h-full"
                  style={{
                    objectFit: mediaItem.metadata.coverViewMode || "contain",
                  }}
                />
              )}
              {isInViewport && overlays && overlays.length > 0 && (
                <RemotionOverlayRenderer
                  overlays={overlays}
                  containerWidth={scaledMediaDimensions.width}
                  containerHeight={scaledMediaDimensions.height}
                  referenceWidth={mediaDimensions.width}
                  referenceHeight={mediaDimensions.height}
                  currentTime={(mediaItem.metadata.coverTimestamp || 0) / 1000}
                  isVideo={true}
                  isPlaying={false}
                  imageUrls={mergedImageUrls}
                  backgroundColor="transparent"
                  layoutEngine={mediaItem.metadata?.layoutEngine}
                  isFreePlan={isFreePlan}
                  wasGeneratedWithWatermark={wasGeneratedWithWatermark}
                  style={{
                    position: "absolute",
                    left: `${scaledMediaDimensions.left}px`,
                    top: `${scaledMediaDimensions.top}px`,
                    width: `${scaledMediaDimensions.width}px`,
                    height: `${scaledMediaDimensions.height}px`,
                    zIndex: 2,
                  }}
                />
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-16 h-16 rounded-full bg-white/25 flex items-center justify-center">
                  <div className="w-0 h-0 border-t-[12px] border-t-transparent border-l-[24px] border-l-white border-b-[12px] border-b-transparent ml-2" />
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        // Show image
        <img
          src={resolvedMediaUrl ?? undefined}
          alt="Media content"
          className="w-full h-full object-contain"
          style={{
            objectFit: mediaItem.metadata.coverViewMode || "contain",
            filter: resolveImageFilterCss(mediaItem.metadata.backgroundFilter),
          }}
          onLoad={() => {
            if (mediaItem.metadata.dimensions) {
              setMediaDimensions({
                width: mediaItem.metadata.dimensions.width,
                height: mediaItem.metadata.dimensions.height,
              });
              setMediaDimensionsReady(true);
            } else {
              const img = new Image();
              img.onload = () => {
                setMediaDimensions({
                  width: img.width,
                  height: img.height,
                });
                setMediaDimensionsReady(true);
              };
              img.src = resolvedMediaUrl ?? "";
            }

            if (onMediaLoad) onMediaLoad();
          }}
        />
      )}

      {/* Custom Controls: Only show for video */}
      {mediaType === "video" && isControlsVisible && videoDuration > 0 && (
        <MediaPlayerControls
          isPlaying={isPlaying}
          currentTime={videoTime}
          duration={totalDuration}
          onPlayPause={() => {
            if (!videoRef.current) return;
            if (videoRef.current.paused) {
              onPlayStateChangeRef.current?.(true);
              videoRef.current.play();
            } else {
              onPlayStateChangeRef.current?.(false);
              videoRef.current.pause();
            }
          }}
          onSeek={(time: number) => {
            if (!videoRef.current) return;

            if (playTimeoutRef.current) {
              clearTimeout(playTimeoutRef.current);
            }

            hasEndedRef.current = false;

            const videoDuration = videoRef.current.duration;

            if (time < videoDuration) {
              videoRef.current.pause();
              videoRef.current.currentTime = time;
              setVideoTime(time);
              onTimeUpdateRef.current?.(time);

              if (isPlaying && time < videoDuration - 0.1) {
                playTimeoutRef.current = setTimeout(() => {
                  videoRef.current?.play();
                }, 150);
              }
            }
          }}
          muted={isMuted}
          onMuteToggle={() => setIsMuted((prev) => !prev)}
        />
      )}

      {/* Hidden audio element */}
      {mediaType === "video" && audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          style={{ display: "none" }}
          preload="auto"
        />
      )}

      {/* Text overlays */}
      {shouldRenderOverlays && (
        <RemotionOverlayRenderer
          overlays={overlays}
          containerWidth={scaledMediaDimensions.width}
          containerHeight={scaledMediaDimensions.height}
          referenceWidth={mediaDimensions.width}
          referenceHeight={mediaDimensions.height}
          currentTime={videoTime}
          isVideo={mediaType === "video"}
          isPlaying={isPlaying}
          imageUrls={mergedImageUrls}
          backgroundColor="transparent"
          layoutEngine={mediaItem.metadata?.layoutEngine}
          isFreePlan={isFreePlan}
          wasGeneratedWithWatermark={wasGeneratedWithWatermark}
          style={{
            position: "absolute",
            left: `${scaledMediaDimensions.left}px`,
            top: `${scaledMediaDimensions.top}px`,
            width: `${scaledMediaDimensions.width}px`,
            height: `${scaledMediaDimensions.height}px`,
          }}
        />
      )}

      {/* Loading indicator */}
      {showLoadingIndicator && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 z-20">
          <div className="bg-black bg-opacity-60 text-white rounded-md px-3 py-2 text-sm flex items-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Loading media...
          </div>
        </div>
      )}
    </div>
  );
}
